import React, { useState, useEffect } from "react";
import "./dataUpload.css";
import {
  Button,
  Form,
  Select,
  Radio,
  Input,
  Upload,
  Card,
  Checkbox,
  Spin,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import axios from "axios";
import DataUploadReview from "./data-upload-review";
import { dataUploadProcess } from "../../../services/data-upload-process";
import { dataUploadDraftSubmit } from "../../../services/data-upload-draft-submit";
import { dataUploadFetchDetails } from "../../../services/data-upload-fetch-details";
import { fileUploadStart } from "../../../services/file-upload-start";
import { fileUploadComplete } from "../../../services/file-upload-complete";
import { RowData } from "../data-transfer";
import { getUserEmail } from "../../../services/utils";
import {
  filterSDTMDeliverables,
  convertSDTM,
  updateDeliverablesWithDescription,
  extractFileNamesFromZip,
} from "./utils";
import { useOpenNotification } from "../../../components/Notification/notification";
import {
  descriptionLabel,
  filesUploadLabel,
  isSupplierRole,
} from "../../../components/Shared/utils";
import {
  validateDeliverables,
  validateZipFiles,
  FileInputError,
} from "../UploadDTS/validator";
import { useNavigate } from "react-router-dom";

const { Item } = Form;
const { TextArea } = Input;
const chunkSize = 100 * 1024 * 1024;
const MAX_CONCURRENT_CHUNK_UPLOADS = 6;
const MAX_CHUNK_RETRY_ATTEMPTS = 3;
const CHUNK_RETRY_DELAY_MS = 1000;
  ;

interface DataUploadProps {
  transferIdOptions: any;
  data: RowData;
  currentPage: string;
}

const DataUpload: React.FC<DataUploadProps> = ({
  transferIdOptions,
  data,
  currentPage,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]); // Specify the type as UploadFile[]
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [zipContentPreview, setZipContentPreview] = useState<string[]>([]);
  const [isReviewPage, setIsReviewPage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>();
  const [dataUploadFormValues, setDataUploadFormValues] = useState<any>();
  const [showResultPage, setShowResultPage] = useState<boolean>(false);
  const [names, setNames] = useState<string[]>([]);
  const formValues = Form.useWatch([], form);
  const userEmail = getUserEmail();
  const { openNotification, contextHolder } = useOpenNotification();
  const navigate = useNavigate();
  const [lastUploadTime, setLastUploadTime] = useState<number | null>(null);

  const handleChange = async (info: UploadChangeParam) => {
    if (lastUploadTime && Date.now() - lastUploadTime < 60000) {
      openNotification(
        "error",
        "Please wait at least 60 seconds between uploads in order to avoid overwriting existing data."
      );
      return;
    }
    const { fileList: newFileList } = info; // Destructure fileList from info
    setFileList(newFileList);
    form.setFieldsValue({ fileInput: newFileList });

    const uploadedFile = newFileList[0]?.originFileObj;
    if (uploadedFile && uploadedFile.name.endsWith(".zip")) {
      const fileBuffer = await uploadedFile.arrayBuffer();
      const fileNamesInZip = extractFileNamesFromZip(fileBuffer);
      setZipContentPreview(fileNamesInZip);
    }
  };

  const transferIdOption =
    transferIdOptions?.Transfer?.map((item: any) => ({
      label: item,
      value: item,
    })) || [];

  useEffect(() => {
    if (formValues?.fileInput?.length) {
      setFileNames(formValues.fileInput.map((file: any) => file.name));
    }
  }, [formValues]);

  useEffect(() => {
    if (
      (currentPage === "data-upload" ||
        currentPage === "finalize-transfer" ||
        currentPage === "reject-transfer") &&
      !isReviewPage
    ) {
      setDataUploadFormValues(formValues);
    }
  }, [formValues, currentPage, isReviewPage]);

  useEffect(() => {
    if (data?.init_deliverables) {
      const deliverablesObject =
        typeof data?.init_deliverables === "string"
          ? JSON.parse(data?.init_deliverables)
          : data?.init_deliverables;
      let filenames = deliverablesObject?.SDTMDeliverables.map(
        (item: any) => item.filename,
      );
      filenames = filenames.includes("Other deliverable(s)")
        ? filenames
        : [...filenames, "Other deliverable(s)"];
      setNames(filenames);
    } else {
      setNames([]);
    }
  }, [data]);

  useEffect(() => {
    dataUploadFetchDetails(data.record_id).then((values) => {
      if (values?.updated_when && values.status === "Supplier Upload Submitted (TEST)") {
        const previousUploadTime = new Date(
          values.updated_when
            .replace(" ", "T")
            .replace(/(\.\d{3})\d+$/, "$1") + "Z"
        ).getTime();
        if (!Number.isNaN(previousUploadTime)) {
          setLastUploadTime(previousUploadTime);
        }
      }

      if (
        values &&
        values.status !== "Supplier Upload Submitted (TEST)" &&
        values.status !== "Finalize Transfer"
      ) {
        const SDTMDeliverables = convertSDTM(values.stdm_deliverables);
        const files =
          values.status !== "Security scan failed" && values.supplier_upload_file_names
            ? existingFiles(values.supplier_upload_file_names[0].fileNames)
            : existingFiles([]);
        setFileList(
          values.status !== "Security scan failed"
            ? (files as UploadFile[])
            : [],
        );
        setInitialValues({
          transferLocation: values.transfer_location ?? 'prod',
          transferId: data.transfer_id,
          SDTMDeliverables,
          description: values.supplier_description,
          fileInput: files,
          rejectionReason: data.rejection_reason,
        });
        setZipContentPreview(
          values.status !== "Security scan failed" && values.supplier_upload_file_names
            ? values.supplier_upload_file_names[0].previewFiles
            : [],
        );
      } else {
        setInitialValues({ transferId: data.transfer_id, transferLocation: "prod" });
      }
    });
  }, [data.record_id, data.transfer_id, data.rejection_reason]);

  const existingFiles = (values: string[]) => {
    return values ? values.map((item: any) => ({ name: item })) : [];
  };

  const uploadChunk = async (chunk: any, url: string, PartNumber: any) => {
    try {
      const response = await axios.put(url, chunk, {
        headers: {
          "Content-Type": "application/octet-stream",
          "Access-Control-Allow-Origin": "*",
        },
      });
      const ETag = response.headers.etag;
      return { PartNumber, ETag };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (error?.response?.status ? `Upload failed (HTTP ${error.response.status})` : null) ||
        error?.message ||
        "Chunk upload failed.";
      return { error: true, message };
    }
  };

  const getChunk = (file: any, index: number) => {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    return file.slice(start, end);
  };

  const uploadChunkWithProgress = async (
    chunk: any,
    url: string,
    index: number,
    totalChunks: any,
  ) => {
    let lastError: string | undefined;
    for (let attempt = 1; attempt <= MAX_CHUNK_RETRY_ATTEMPTS; attempt += 1) {
      const response = await uploadChunk(chunk, url, index);
      if (response && !(response as any).error) {
        return response;
      }
      lastError = (response as any)?.message;
      if (attempt < MAX_CHUNK_RETRY_ATTEMPTS) {
        await sleep(CHUNK_RETRY_DELAY_MS * attempt);
      }
    }
    return { error: true, message: lastError || "Chunk upload failed after retries." };
  };

  const uploadChunksInParallel = async (
    file: any,
    urls: any[],
    totalChunks: number,
    concurrency: number = MAX_CONCURRENT_CHUNK_UPLOADS,
  ) => {
    const parts: any[] = new Array(totalChunks).fill(null);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < totalChunks) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        const chunk = getChunk(file, currentIndex);
        const urlEntry = urls[currentIndex];
        const url = Array.isArray(urlEntry) ? urlEntry[1] : urlEntry;
        const uploadResult = await uploadChunkWithProgress(
          chunk,
          url,
          currentIndex + 1,
          totalChunks,
        );
        parts[currentIndex] = uploadResult;
      }
    };

    const workerCount = Math.max(1, Math.min(concurrency, totalChunks));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    return parts;
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const waitForSubmitCompletion = async (): Promise<"UPLOADED" | "FAILED" | "QUARANTINED" | "TIMEOUT"> => {
    for (let i = 0; i < 720; i++) { // up to ~10 mins (120 * 5s)
      const values = await dataUploadFetchDetails(data.record_id);
      if (values?.file_upload_status === "UPLOADED") return "UPLOADED";
      if (values?.file_upload_status === "FAILED") return "FAILED";
      if (
        values?.file_upload_status === "QUARANTINED" ||
        values?.file_upload_status === "QURATINED"
      ) {
        return "QUARANTINED";
      }
      await sleep(10000);
    }
    return "TIMEOUT";
  };

  const uploadSingleFile = async (file: UploadFile, values: any) => {
    const fileToUpload = file.originFileObj;
    if (!fileToUpload) return undefined;

    const fileData = {
      fileName: fileToUpload?.name,
      fileSize: fileToUpload?.size,
      compoundNumber: data?.msd,
      protocolNumber: data?.protocol_number,
      transferId: data?.transfer_id.split("-")[0],
      environment: dataUploadFormValues.transferLocation,
    };

    const resUpdatedUrls = await fileUploadStart(fileData);
    if (!resUpdatedUrls || (resUpdatedUrls as any)?.error) {
      const errorMsg = (resUpdatedUrls as any)?.message || "Failed to file upload.";
      openNotification("error", errorMsg);
      setLoading(false);
      setLoadingSubmit(false);
      return false;
    }
    const uploadUrls = resUpdatedUrls?.uploadUrls;
    const uploadId = resUpdatedUrls?.uploadId;
    const finalKey = resUpdatedUrls?.key;

    if (!finalKey || !uploadId || !uploadUrls.length) {
      openNotification("error", "Invalid upload start response");
      setLoading(false);
      setLoadingSubmit(false);
      return false;
    }

    const totalChunks = Math.ceil(fileToUpload.size / chunkSize);
    const parts = await uploadChunksInParallel(
      fileToUpload,
      uploadUrls,
      totalChunks,
    );

    if (parts.some((part) => (part === null || (part as any)?.error))) {
      const failedPart = parts.find((p) => p === null || (p as any)?.error);
      const errorMsg = (failedPart as any)?.message || "Failed to upload file.";
      openNotification("error", errorMsg);
      setLoading(false);
      setLoadingSubmit(false);
      return false;
    }

    const response = await fileUploadComplete({
      objectName: finalKey,
      uploadId,
      parts,
      compoundNumber: data.msd,
      protocolNumber: data.protocol_number,
      transferId: data.transfer_id.split("-")[0],
      environment: dataUploadFormValues.transferLocation,
    });

    if (!response || (response as any)?.error) {
      const errorMsg =
        (response as any)?.message ||
        "Upload incomplete: no valid uploaded parts found. File may have been removed/quarantined locally. Please re-upload.";
      openNotification("error", errorMsg);
      setLoading(false);
      setLoadingSubmit(false);
      return false;
    }

    // Return final key for /process
    return finalKey;
  };

  const processUploadedFiles = async (
    filteredFileNames: string[],
    values: any,
  ) => {
    if (filteredFileNames.length === 0) return Promise.resolve(true);
    const response = await dataUploadProcess({
      fileNames: filteredFileNames,
      fileCount: filteredFileNames.length,
      environment: dataUploadFormValues.transferLocation,
      compound: data.msd,
      transferId: data.transfer_id.split("-")[0],
      protocol: data?.protocol_number,
      recordId: data.record_id,
    });
    if (!response || response?.error) {
      const errorMsg = response?.message || "Failed to upload files.";
      openNotification("error", errorMsg);
      setLoading(false);
      setLoadingSubmit(false);
      return false;
    }
    return true;
  };

  const saveDraft = async (isSubmitted = false) => {
    const deliverables = updateDeliverablesWithDescription(
      filterSDTMDeliverables(dataUploadFormValues.SDTMDeliverables),
      data?.init_deliverables?.SDTMDeliverables,
    );

    try {
      await dataUploadDraftSubmit({
        email: userEmail,
        recordId: data.record_id,
        transferId: data.transfer_id,
        transferLocation: dataUploadFormValues.transferLocation,
        deliverables,
        description: dataUploadFormValues.description,
        fileNames: [{ fileNames, previewFiles: zipContentPreview }],
        status: isSubmitted
          ? dataUploadFormValues.transferLocation === "test"
            ? "Supplier Upload Submitted (TEST)"
            : "Supplier Upload Submitted (PROD)"
          : "Supplier Upload Save Draft",
      });

      if (isSubmitted) {
        openNotification("info", "File scan is in progress. Please wait");
        const submitStatus = await waitForSubmitCompletion();

        if (submitStatus === "QUARANTINED") {
          setLoadingSubmit(false);
          openNotification("error", "The security scan for your uploaded package has failed. The package has been moved to a quarantine folder in CDIG S3. Please re-upload a clean package or contact your administrator for further assistance.");
          setTimeout(() => navigate("/dashboard"), 5000);
          return;
        }

        if (submitStatus !== "UPLOADED") {
          setLoadingSubmit(false);
          openNotification("error", "Submit processing failed or timed out.");
          return;
        }

        setShowResultPage(true);
        setLoadingSubmit(false);
        return;
      }

      openNotification("success", "Draft is saved successfully!");
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setLoadingSubmit(false);
      openNotification("error", error?.message ?? "Failed to save file upload data");
      if (
        error?.message === "The security scan for your uploaded package has failed. The package has been moved to a quarantine folder in CDIG S3. Please re-upload a clean package or contact your administrator for further assistance."
      ) {
        setTimeout(() => navigate("/dashboard"), 5000);
      }
    }
  };

  const handleEdit = () => {
    setIsReviewPage(false);
  };

  const handleReview = async (isDraft: boolean) => {
    if (fileList.length === 0) {
      form.setFields([{ name: "fileInput", errors: ["This is a required field"] }]);
    }
    try {
      const values = await form.validateFields();
      if (isDraft) setLoading(true);
      else setLoadingSubmit(true);

      const uploadedKeys: string[] = [];

      if (fileList.length > 0) {
        for (const file of fileList) {
          const uploaded = await uploadSingleFile(file, values);
          if (uploaded === false) return;
          if (typeof uploaded === "string") uploadedKeys.push(uploaded);
        }

        if (uploadedKeys.length > 0) {
          const processed = await processUploadedFiles(uploadedKeys, values);
          if (!processed) return;
        }
      }
        const isSubmitted = !isDraft;
        if (isSubmitted) setIsReviewPage(true);
        await saveDraft(isSubmitted);
      } catch {
        return;
      }
    };

  const handleNext = async () => {
    if (fileList.length === 0) {
      form.setFields([{ name: "fileInput", errors: ["This is a required field"] }]);
    }
    try {
      await form.validateFields(); // Validate the form fields
      setIsReviewPage(true); // Proceed only if validation succeeds
    } catch (error) {
      return;
    }
  };

  const handleCheckboxChange = (checked: any, name: any) => {
    if (!checked) {
      // Clear the comment field when checkbox is unchecked
      form.setFields([
        {
          name: ["SDTMDeliverables", name, "comment"],
          value: undefined,
        },
      ]);
    }
    const currentDeliverables = form.getFieldValue("SDTMDeliverables") || {};
    form.setFieldsValue({
      SDTMDeliverables: {
        ...currentDeliverables,
        [name]: {
          ...currentDeliverables[name],
          checked,
        },
      },
    });
    form.validateFields(["SDTMDeliverables"]);
  };

  return (
    <>
      {!isReviewPage ? (
        <>
          {initialValues ? (
            <div className="section-right">
              <Form
                layout="vertical"
                style={{
                  textAlign: "left",
                }}
                initialValues={initialValues}
                form={form}
                disabled={!isSupplierRole()}
                scrollToFirstError
                requiredMark={false}
              >
                {data.status === "Reject Transfer" && (
                  <div className="section-transfer">
                    <div className="section-title">Comment</div>
                    <Item
                      label="Requester"
                      name="rejectionReason"
                      style={{ margin: "1rem 0" }}
                    >
                      <TextArea
                        rows={1}
                        value={data.rejection_reason}
                        autoSize={{ minRows: 1 }}
                        disabled={true}
                        style={{
                          border: "none",
                          borderBottom: "1px solid #d9d9d9",
                          borderRadius: 0,
                          outline: "none",
                          boxShadow: "none",
                        }}
                      />
                    </Item>
                  </div>
                )}
                <div className="section-transfer">
                  <div className="section-title">Transfer</div>
                  <Item
                    label={
                      <>
                        <span style={{ color: "red", fontWeight: 400, fontSize: 20, lineHeight: "28px", letterSpacing: 0 }}>*</span>&nbsp;Transfer Location&nbsp;
                      </>
                    }
                    name="transferLocation"
                    tooltip=" Production uploads are for the actual transfer submission. Test uploads are used by MSD to assess the transfer’s format and contents before production submission."
                    style={{ margin: "2rem 0" }}
                    rules={[
                      { required: true, message: "This is a required field" },
                    ]}
                  >
                    <Radio.Group
                      name="radiogroup"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                      options={[
                        { value: "prod", label: "Production" },
                        { value: "test", label: "Test" },
                      ]}
                    />
                  </Item>
                </div>

                <div className="section-transfer">
                  <div className="section-title">Deliverables</div>
                  <Item
                    label={
                      <>
                        <span style={{ color: "red", fontWeight: 400, fontSize: 20, lineHeight: "28px", letterSpacing: 0 }}>*</span>&nbsp;Transfer ID&nbsp;
                      </>
                    }
                    name="transferId"
                    style={{ margin: "2rem 0", width: "40%" }}
                    rules={[
                      { required: true, message: "This is a required field" },
                    ]}
                  >
                    <Select
                      id=""
                      placeholder=""
                      options={transferIdOption}
                      disabled={true}
                    />
                  </Item>
                  <Item
                    label={
                      <>
                        {formValues?.transferLocation !== "test" && <span style={{ color: "red", fontWeight: 400, fontSize: 20, lineHeight: "28px", letterSpacing: 0 }}>*</span>}&nbsp;Transfer Deliverables&nbsp;
                      </>
                    }
                    required={formValues?.transferLocation !== "test"}
                  >
                    {names.map((name) => (
                      <div
                        key={name}
                        style={{
                          marginBottom: 10,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Item
                          name={["SDTMDeliverables", name, "checked"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <Checkbox
                            onChange={(e) =>
                              handleCheckboxChange(e.target?.checked, name)
                            }
                          >
                            {name}
                          </Checkbox>
                        </Item>

                        <Item
                          shouldUpdate={(prev, curr) =>
                            prev.SDTMDeliverables?.[name]?.checked !==
                            curr.SDTMDeliverables?.[name]?.checked
                          }
                          noStyle
                        >
                          {({ getFieldValue }) =>
                            getFieldValue([
                              "SDTMDeliverables",
                              name,
                              "checked",
                            ]) ? (
                              <Item
                                name={["SDTMDeliverables", name, "comment"]}
                                noStyle
                              >
                                <Input
                                  placeholder="Enter comment"
                                  style={{ width: "55%", marginTop: 10 }}
                                />
                              </Item>
                            ) : null
                          }
                        </Item>
                      </div>
                    ))}
                    <Item
                      name="SDTMDeliverables"
                      noStyle
                      rules={[
                        {
                          validator: (_, deliverables) =>
                            validateDeliverables(
                              deliverables,
                              true,
                              formValues,
                            ),
                        },
                      ]}
                    >
                      <Input type="hidden" />
                    </Item>
                  </Item>
                </div>
                <div className="section-transfer">
                  <div className="section-title">
                    {
                      <>
                        <span style={{ color: "red" }}>* </span>Description &nbsp;
                      </>
                    }
                  </div>
                  <Item
                    name="description"
                    required={false}
                    label={descriptionLabel(true)}
                    rules={[
                      { required: true, message: "This is a required field" },
                    ]}
                  >
                    <TextArea rows={4} />
                  </Item>
                </div>
                <div className="section-transfer">
                  <div className="section-title">
                    {
                      <>
                        <span style={{ color: "red" }}>* </span>Files &nbsp;
                      </>
                    }
                  </div>
                  <Item
                    name="fileInput"
                    required={false}
                    label={filesUploadLabel()}
                    rules={[
                      {
                        required: true,
                        message: "This is a required field",
                      },
                      {
                        validator: validateZipFiles(fileList),
                      },
                    ]}
                    help={FileInputError(fileList, form)}
                    validateStatus={
                      form.getFieldError("fileInput").length > 0
                        ? "error"
                        : undefined
                    }
                  >
                    <Upload
                      fileList={fileList}
                      onChange={handleChange}
                      beforeUpload={() => false}
                      accept=".zip"
                    >
                      <Button icon={<UploadOutlined />}>Upload</Button>
                    </Upload>
                  </Item>
                </div>
                <div className="save-next-button">
                  <div className="save-next-botton-content"></div>
                  <Card variant="borderless">
                    <Button
                      color="cyan"
                      variant="solid"
                      className="next-button"
                      data-testid="button-review"
                      onClick={handleNext}
                    >
                      {" "}
                      Review{" "}
                    </Button>
                    <Button
                      data-testid="button-save-draft"
                      onClick={() => handleReview(true)}
                      loading={loading}
                    >
                      {" "}
                      Save Draft{" "}
                    </Button>
                  </Card>
                </div>
              </Form>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
              }}
            >
              <Spin />
            </div>
          )}
        </>
      ) : (
        <DataUploadReview
          formValues={formValues}
          handleEdit={handleEdit}
          data={data}
          userEmail={userEmail}
          fileNames={fileNames}
          handleReview={handleReview}
          submitPage={showResultPage}
          loadingSubmit={loadingSubmit}
          zipContentPreview={zipContentPreview}
        />
      )}
      {contextHolder}
    </>
  );
};

export default DataUpload;
